import { ScreenUtils } from "../../helper/screen.helper";
import * as _ from "lodash";
import * as angular from "angular";
import { CanvassStatusDataProvider } from "../../data-provider/canvass-status.dp";
import { CanvassStatus } from "../../models/canvass-status.model";
import { User } from "../../models/user.model";
import { UserService } from "../../services/users.service";

declare const google;

export class MarkerService {
    private $filter: any;
    private canvassStatusDataProvider: CanvassStatusDataProvider;

    public canvassStatus: CanvassStatus;
    public statuses: CanvassStatus[] = [];

    public filepath: string | null = null;
    public labelContent: string | null = null;
    public labelStyle: any = null;
    public icon: any | null = null;
    public anchor: string | null = null;
    public fileName: string | null = null;
    public map: any;
    public screen: ScreenUtils;
    public avatarCache = new Map();

    private userService: UserService;
    private photoPromises = new Map<string, any>();

    private fontStack: string =
        "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif";

    constructor(
        $filter: any,
        ScreenUtils: ScreenUtils,
        CanvassStatusDataProvider: CanvassStatusDataProvider,
        UserService: UserService
    ) {
        this.$filter = $filter;
        this.canvassStatusDataProvider = CanvassStatusDataProvider;
        this.canvassStatus = new CanvassStatus();
        this.screen = ScreenUtils;
        this.userService = UserService;

        this.canvassStatusDataProvider
            .walkCanvassStatuses()
            .then((data: CanvassStatus[]) => {
                this.statuses = data;
            });
    }

    setMap(map: any): void {
        this.map = map;
    }

    getMarkerOption(
        marker: any,
        selectedMarker?: any,
        reset?: boolean
    ): object {
        let alias = "";
        if (
            marker.size == "1" &&
            !reset &&
            selectedMarker &&
            marker.hid == selectedMarker.model.hid
        ) {
            selectedMarker.model = marker;
            return {
                icon: {
                    url: "/img/map/markers/dyellow.svg",
                    scaledSize: this.getScaledSize(
                        this.map && this.map.zoom ? this.map.zoom : null,
                        true
                    ),
                },
            };
        }
        for (let i = 0; i < this.statuses.length; i++) {
            if (marker.canvass_status == 7) {
                alias = "green";
            } else if (marker.canvass_status == 8) {
                alias = "red";
            } else if (marker.canvass_status == this.statuses[i].id) {
                alias = this.canvassStatus.getColorAlias(
                    this.statuses[i].color
                );
            }
        }
        marker.type = "house";
        return this.markerOption(marker, alias, selectedMarker, reset);
    }

    generateClusterMarker(user: User, lat: object, lng: object): object {
        user.type = "user";
        return {
            id: "cluster" + user.uid,
            options: this.markerOption(user),
            coords: {
                latitude: lat,
                longitude: lng,
            },
        };
    }

    getScaledSize(zoom?: number, selected?: boolean): object {
        let multiplier: number = selected ? 1.7 : 1.1;

        if (this.screen.smallScreen()) {
            multiplier *= 1.6;
        }

        if (zoom < 14) {
            return new google.maps.Size(
                13.846 * multiplier,
                18.467 * multiplier
            );
        } else if (zoom < 15) {
            return new google.maps.Size(
                14.575 * multiplier,
                19.439 * multiplier
            );
        } else if (zoom < 16) {
            return new google.maps.Size(
                15.3425 * multiplier,
                20.463 * multiplier
            );
        } else if (zoom < 17) {
            return new google.maps.Size(16.15 * multiplier, 21.54 * multiplier);
        } else if (zoom < 18) {
            return new google.maps.Size(
                18.575 * multiplier,
                25.02 * multiplier
            );
        } else if (zoom < 19) {
            return new google.maps.Size(21 * multiplier, 28.5 * multiplier);
        } else if (zoom < 20) {
            return new google.maps.Size(25.2 * multiplier, 34.2 * multiplier);
        } else if (zoom < 21) {
            return new google.maps.Size(28 * multiplier, 38 * multiplier);
        }

        return new google.maps.Size(30.8 * multiplier, 41.8 * multiplier);
    }

    generateUserMarker(
        user: User | any,
        lat: object,
        lng: object,
        cloneUser: boolean = true,
        zIndex: number = null
    ): User | any {
        let marker: any;
        if (cloneUser) {
            marker = _.cloneDeep(user);
        } else {
            marker = {
                uid: user.uid,
                ufname: user.ufname,
                ulname: user.ulname,
                uemail: user.uemail,
                uphonenumber: user.uphonenumber,
                ucountrycode: user.ucountrycode,
                color: user.color,
                permission: user.permission,
                images: user.images,
                address: user.address,
                lat: lat,
                lng: lng,
                logins: user.logins,
                lastlogin: user.lastlogin,
                useragent: user.udevice,
                uos: user.uos,
                ujoindate: user.ujoindate,
            };
        }

        marker.coords = {
            latitude: lat,
            longitude: lng,
        };

        marker.id = "user" + user.uid;

        let title: string;
        if (cloneUser) {
            title = user.ufname + " " + user.ulname;
        } else {
            title = user.ufname;
        }
        marker.options = {
            icon: {
                url: this.generateUserMarkerSVG(user),
                scaledSize: {
                    width: 48,
                    height: 55,
                },
            },
            title: title,
            userIcon: true,
            pinIcon: true,
            optimized: true,
        };
        if (zIndex) {
            marker.options.zIndex = zIndex;
        }

        if (!cloneUser) {
            let getRandomOffset: () => number = (): number =>
                Math.random() * 0.00006 - 0.00003;

            marker.coords = {
                latitude: Number(lat) + getRandomOffset(),
                longitude: Number(lng) + getRandomOffset(),
            };
        }

        this.applyUserPhotoIcon(marker, user);

        return marker;
    }

    /**
     * Swaps the initials pin for one containing the user's profile photo.
     * The photo has to be inlined as base64 (SVGs loaded as marker icons
     * cannot reference external URLs), so it is fetched through the API.
     * Falls back silently to the initials pin on any failure.
     */
    applyUserPhotoIcon(marker: any, user: User | any): void {
        if (
            !user ||
            !(
                user.hasprofilepic == true ||
                user.hasprofilepic == 1 ||
                user.hasprofilepic == "1"
            )
        ) {
            return;
        }

        const isOnline: boolean =
            typeof user.isOnline === "function" ? user.isOnline() : false;
        const cacheKey: string = `photo_${user.uid}_${isOnline}_bubble_v1`;

        if (this.avatarCache.has(cacheKey)) {
            marker.options.icon.url = this.avatarCache.get(cacheKey);
            return;
        }

        this.getUserPhoto(user.uid)
            .then((photo: string | null) => {
                if (!photo) return;
                const url: string = this.generateUserPhotoMarkerSVG(
                    user,
                    photo,
                    isOnline
                );
                this.avatarCache.set(cacheKey, url);
                marker.options.icon.url = url;
            })
            .catch(() => {
                // keep the initials pin
            });
    }

    private getUserPhoto(uid: string | number): any {
        const key = String(uid);
        if (!this.photoPromises.has(key)) {
            this.photoPromises.set(
                key,
                this.userService.profileImage(uid).then(
                    (response: any) => {
                        const json = response.data;
                        return json && json.success && json.data
                            ? json.data.image
                            : null;
                    },
                    () => null
                )
            );
        }
        return this.photoPromises.get(key);
    }

    generateUserPhotoMarkerSVG(
        user: any,
        photoDataUri: string,
        isOnline: boolean
    ): string {
        const bgColor: string = user.color || "#e0e0e0";

        const svgString: string = `
    <svg width="52" height="60" viewBox="0 0 52 60" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
        <defs>
            <clipPath id="avatarClip">
                <circle cx="26" cy="24" r="16" />
            </clipPath>
        </defs>

        <!-- 1. Ground shadow -->
        <ellipse cx="26" cy="56.5" rx="7" ry="2.2" fill="rgba(0,0,0,0.18)" />

        <!-- 2. Pointer -->
        <path d="M17.5,40 L26,56 L34.5,40 Z" fill="${bgColor}" />

        <!-- 3. Avatar rings, colored backing in case the photo has transparency -->
        <circle cx="26" cy="24" r="20" fill="${bgColor}" />
        <circle cx="26" cy="24" r="18" fill="#FFFFFF" />
        <circle cx="26" cy="24" r="16" fill="${bgColor}" />

        <!-- 4. Profile photo, clipped to the circle -->
        <image x="10" y="8" width="32" height="32"
               clip-path="url(#avatarClip)"
               preserveAspectRatio="xMidYMid slice"
               href="${photoDataUri}"
               xlink:href="${photoDataUri}" />

        <!-- Status Dot -->
        ${this.onlineDot(isOnline)}
    </svg>
    `
            .trim()
            .replace(/\s+/g, " ");

        return (
            "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svgString)
        );
    }

    generateCampaignDivisionMarker(obj: any): User | any {
        let marker: any;
        marker = _.cloneDeep(obj);

        marker.coords = {
            latitude: obj.latitude,
            longitude: obj.longitude,
        };

        if (obj.dataType == "campaign") {
            marker.id = "campaign" + obj.campaignid;
        } else {
            marker.id = "division" + obj.divisionid;
        }
        marker.options = {
            icon: {
                url: this.generateCampaignDivisionMarkerSVG(
                    obj.name,
                    obj.onlineUsers
                ),
                scaledSize: new google.maps.Size(180, 60),
            },
            userIcon: true,
        };

        return marker;
    }

    generateCampaignDivisionMarkerSVG(name: string, count: number): string {
        // 1. Define colors
        const primaryColor: string = count > 0 ? "#0b986d" : "#6B7280";
        const bgColor = "#FFFFFF";
        const textColor = "#1F2937";

        // 2. Truncate name
        const displayName: string =
            name.length > 18 ? name.substring(0, 14) + "..." : name;

        // 3. Define Dimensions
        const width = 160;
        const boxHeight = 50; // Height of the rectangular part
        const arrowHeight = 10; // Height of the point
        const totalHeight: number = boxHeight + arrowHeight + 10; // +10 buffer for shadow/stroke

        // 4. Calculate Path Coordinates for a Rounded Box with a Bottom Arrow
        // We use a path so the border stroke is continuous around the arrow
        const r = 10; // Corner radius
        const w: number = width;
        const h: number = boxHeight;
        const ah: number = arrowHeight;
        const mid: number = w / 2;

        // Path Logic:
        // Move to Top-Left -> Line to Top-Right -> Curve -> Line to Bottom-Right -> Curve
        // -> Line to Arrow Start -> Line to Tip -> Line to Arrow End
        // -> Line to Bottom-Left -> Curve -> Line to Top-Left -> Curve
        const path = `
        M ${r + 2},2
        L ${w - r - 2},2
        Q ${w - 2},2 ${w - 2},${r + 2}
        L ${w - 2},${h - r - 2}
        Q ${w - 2},${h - 2} ${w - r - 2},${h - 2}
        L ${mid + 10},${h - 2}
        L ${mid},${h - 2 + ah}
        L ${mid - 10},${h - 2}
        L ${r + 2},${h - 2}
        Q 2,${h - 2} 2,${h - r - 2}
        L 2,${r + 2}
        Q 2,2 ${r + 2},2
        Z
    `;

        // 5. Create the SVG String
        const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${totalHeight}" viewBox="0 0 ${width} ${totalHeight}">
        <filter id="shadow" x="-10%" y="-10%" width="120%" height="120%">
            <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#000" flood-opacity="0.3"/>
        </filter>

        <!-- Main Shape (Box + Arrow merged) -->
        <path d="${path}" fill="${bgColor}" stroke="${primaryColor}" stroke-width="2" filter="url(#shadow)"/>

        <!-- Campaign Name -->
        <text x="50%" y="20" font-family="${this.fontStack}" font-size="15" font-weight="bold" fill="${textColor}" text-anchor="middle" dominant-baseline="middle">
            ${displayName}
        </text>

        <!-- Online Count Section -->

        <text x="50%" y="36" font-family="${this.fontStack}"  font-size="13" font-weight="600"
              fill="${primaryColor}" text-anchor="middle"  dominant-baseline="middle">
            ${count} Users Online
        </text>
    </svg>`;

        // 6. Return Data URI
        // Note: Since the arrow tip is exactly at the bottom-center of the SVG,
        // Google Maps default anchoring will handle this correctly without needing an object.
        return "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg);
    }

    generateUserMarkerSVG(user: any): string {
        const isOnline: boolean =
            typeof user.isOnline === "function" ? user.isOnline() : false;
        const cacheKey: string = `${user.ufname}_${user.ulname}_${user.color}_${isOnline}_bubble_v1`;

        if (this.avatarCache.has(cacheKey)) {
            return this.avatarCache.get(cacheKey);
        }

        const initials: string =
            (user.ufname?.charAt(0) || "") + (user.ulname?.charAt(0) || "");
        const bgColor: string = user.color || "#e0e0e0";

        const svgString: string = `
    <svg width="52" height="60" viewBox="0 0 52 60" xmlns="http://www.w3.org/2000/svg">
        <!-- 1. Ground shadow -->
        <ellipse cx="26" cy="56.5" rx="7" ry="2.2" fill="rgba(0,0,0,0.18)" />

        <!-- 2. Pointer -->
        <path d="M17.5,40 L26,56 L34.5,40 Z" fill="${bgColor}" />

        <!-- 3. Avatar rings -->
        <circle cx="26" cy="24" r="20" fill="${bgColor}" />
        <circle cx="26" cy="24" r="18" fill="#FFFFFF" />
        <circle cx="26" cy="24" r="16" fill="${bgColor}" />

        <!-- 4. Initials -->
        <text x="26" y="24" dy="0.35em"
              text-anchor="middle"
              fill="#FFFFFF"
              font-family="${this.fontStack}"
              font-weight="bold"
              font-size="14">
            ${initials}
        </text>

        <!-- Status Dot -->
        ${this.onlineDot(isOnline)}
    </svg>
    `
            .trim()
            .replace(/\s+/g, " ");

        const dataUri: string =
            "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svgString);
        this.avatarCache.set(cacheKey, dataUri);

        return dataUri;
    }

    private onlineDot(isOnline: boolean): string {
        if (!isOnline) return "";
        return `<circle cx="41.5" cy="9" r="6.5" fill="#2dd58a" stroke="#FFFFFF" stroke-width="2" />`;
    }

    markerOption(
        marker: any,
        colorAlias?: string,
        selectedMarker?: any,
        reset?: boolean
    ): object {
        this.labelStyle = {
            fontWeight: "700",
            fontSize: "14px",
            color: "#FFFFFF",
            fontFamily: "Inter",
            letterSpacing: ".6px",
            width: "50px",
            textAlign: "center",
        };
        this.filepath = "/img/map/markers/";
        this.fileName = "m";
        marker.cluster = true;

        if (
            (marker.size == 1 && marker.type == "user") ||
            (marker.coords &&
                marker.first_house &&
                this.coordsVeryClose(marker.coords, marker.first_house))
        ) {
            marker.cluster = false;
            this.fileName = "p";
        }

        if (marker.type == "house" || marker.type == "user") {
            let intsize: number = parseInt(marker.size);
            if (intsize == 1) {
                let url = "";
                if (!marker.canvass_status) {
                    url = this.filepath + "dgrey.svg";
                } else {
                    url = this.filepath + "d" + colorAlias + ".svg";
                }
                return {
                    icon: {
                        url: url,
                        scaledSize: this.getScaledSize(
                            this.map && this.map.zoom ? this.map.zoom : null
                        ),
                    },
                };
            }

            if (!marker.cluster) {
                if (
                    !reset &&
                    selectedMarker &&
                    marker.hid == selectedMarker.model.hid
                ) {
                    selectedMarker.model = marker;
                    return {
                        icon: {
                            url: this.generateCircleIcon(
                                selectedMarker.model.size,
                                true
                            ),
                            scaledSize: new google.maps.Size(34, 34),
                            zIndex: 9999,
                        },
                    };
                }
                return {
                    icon: {
                        url: this.generateCircleIcon(intsize),
                    },
                };
            }

            if (intsize < 10) {
                let thisMarkerWidth = marker.cluster ? "34px" : "25px";
                this.setLabelVars(
                    marker,
                    "17 42",
                    thisMarkerWidth,
                    "8px 0 10px 0",
                    "#4986e7",
                    "1",
                    "#4986e7",
                    marker.id
                );
            } else if (intsize < 100) {
                this.setLabelVars(
                    marker,
                    "21 47",
                    "40px",
                    "10px 0 11px 0",
                    "#ff982b",
                    "2",
                    "#ff982b",
                    marker.id
                );
            } else if (intsize < 1000) {
                this.setLabelVars(
                    marker,
                    "24 56",
                    "47px",
                    "15px 0",
                    "#d0021b",
                    "3",
                    "#d0021b",
                    marker.id
                );
            } else if (intsize < 10000) {
                this.setLabelVars(
                    marker,
                    "31 68",
                    "60px",
                    "21px 0",
                    "#ff1761",
                    "4",
                    "#ff1761",
                    marker.id
                );
            } else if (intsize < 20000) {
                this.setLabelVars(
                    marker,
                    "35 78",
                    "68px",
                    "25px 0",
                    "#8d68c6",
                    "5",
                    "#8d68c6",
                    marker.id
                );
            } else if (intsize < 50000) {
                this.setLabelVars(
                    marker,
                    "38 88",
                    "76px",
                    "30px 0 29px 0",
                    "#953063",
                    "6",
                    "#953063",
                    marker.id
                );
            } else if (intsize < 100000) {
                this.setLabelVars(
                    marker,
                    "38 88",
                    "76px",
                    "30px 0",
                    "#2c3f50",
                    "7",
                    "#2c3f50",
                    marker.id
                );
            } else {
                this.setLabelVars(
                    marker,
                    "38 88",
                    "78px",
                    "30px 0",
                    "#d33357",
                    "8",
                    "#d33357",
                    marker.id
                );
            }
            return {
                labelAnchor: this.anchor,
                labelStyle: this.labelStyle,
                labelContent: this.labelContent,
                icon: this.icon,
                zIndex: parseInt(marker.size),
            };
        } else {
            return {
                clickable: false,
                icon: { url: this.filepath + "pin blue gradient.png" },
            };
        }
    }

    generateCircleIcon(number: number, isSelected: boolean = false): string {
        let circleColor: string = "#4285F4";
        const textColor = "#ffffff";
        let fontSize: string = "14";
        let numberString: string = number.toString();

        if (number > 10) {
            circleColor = "#e78323";
        }

        if (number > 99) {
            numberString = "99+";
            fontSize = "13";
            circleColor = "#e72323";
        }

        let strokeColor: string = this.darkenHex(circleColor, 15);

        if (isSelected) {
            circleColor = this.darkenHex(circleColor, 15);
            strokeColor = this.darkenHex(strokeColor, 15);
        }

        // Create an SVG string
        // CX/CY are center, R is radius. Text is centered.
        const svg = `
            <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30">
              <circle cx="15" cy="15" r="14" fill="${circleColor}" stroke="${strokeColor}" stroke-opacity=".85" stroke-width="1" />
              <text x="50%" y="50%" text-anchor="middle" dy=".35em" font-family="${this.fontStack}"  font-weight="500" font-size="${fontSize}" fill-opacity="0.95" fill="${textColor}">
                ${numberString}
              </text>
            </svg>
          `;

        // Encode the SVG string to a Data URI
        const url: string =
            "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg);

        return url;
    }

    darkenHex(hex: string, percent: number): string {
        // 1. Remove the hash if it exists
        hex = hex.replace(/^\s*#|\s*$/g, "");

        // 2. Handle shorthand hex (e.g., "f00" -> "ff0000")
        if (hex.length === 3) {
            hex = hex.replace(/(.)/g, "$1$1");
        }

        // 3. Convert to RGB
        let r: number = parseInt(hex.substr(0, 2), 16);
        let g: number = parseInt(hex.substr(2, 2), 16);
        let b: number = parseInt(hex.substr(4, 2), 16);

        // 4. Calculate the darkening factor (0.1 means 10%)
        // We strictly floor the value to ensure integer RGBs
        const amount: number = Math.floor((percent / 100) * 255);

        // 5. Darken (subtract the amount, but don't go below 0)
        // Note: You can also multiply (r * (1 - percent/100)) for a different "flavor" of dark
        // But subtracting from absolute brightness is the standard "darken" behavior
        r = Math.max(0, r - amount);
        g = Math.max(0, g - amount);
        b = Math.max(0, b - amount);

        // 6. Convert back to Hex and Pad with 0 if necessary
        const toHex = (n) => n.toString(16).padStart(2, "0");

        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    }

    coordsVeryClose(round_coords: any, first_house: any): boolean {
        const latDiff = round_coords.latitude - first_house.latitude;
        const lngDiff = round_coords.longitude - first_house.longitude;

        return (
            latDiff > -0.00005 &&
            latDiff < 0.00005 &&
            lngDiff > -0.00005 &&
            lngDiff < 0.00005
        );
    }

    setLabelVars(
        marker: any,
        labelAnchor: string,
        width: string,
        padding: string,
        color: string,
        iconNum: string,
        hex: string,
        zIndex: number
    ): void {
        let number = "";
        if (!marker.cluster && marker.size > 99) {
            number = "99+";
        } else if (marker.size) {
            number = this.$filter("number")(marker.size.toString());
        }

        if (marker.name) {
            this.labelContent =
                "<div style='border-radius: 50px; padding: 19px 3px; background: " +
                marker.color +
                ";opacity: .96  '>" +
                "<div style=' white-space: nowrap; overflow: hidden;text-overflow: ellipsis;'>" +
                marker.name +
                "</div>" +
                "<div>" +
                this.$filter("number")(marker.stats.houses) +
                "</div>" +
                "</div>";

            this.labelStyle.width = "74px";
            this.icon = {
                url:
                    this.filepath +
                    "divisions/" +
                    marker.color.substr(1) +
                    ".png",
            };
            this.anchor = "37 88";
        } else {
            if (marker.cluster) {
                this.anchor = labelAnchor;
                this.labelStyle.width = width;
                this.icon = {
                    url: this.filepath + this.fileName + iconNum + ".png",
                };
            } else {
                this.anchor = number == "99+" ? "13 25" : "13 25";
                let fontSize = number == "99+" ? "12" : "12";
                let numberStyle = number == "99+" ? "margin-left: -2px" : "";
                let size =
                    number == "99+"
                        ? "height: 20px; width: 19px;"
                        : "height: 18px; width: 17px;";
                this.labelStyle.width = "30px";
                this.icon = {
                    url: this.filepath + this.fileName + iconNum + ".svg",
                    scaledSize: {
                        width: 20,
                        height: 20,
                    },
                };

                this.labelContent =
                    "<div style='border-radius: 50px; " +
                    size +
                    " padding: " +
                    "4px" +
                    "; z-index: " +
                    zIndex +
                    "; opacity: .98; background: " +
                    hex +
                    "; " +
                    "border: solid rgba(0,0,0,0.38) 1px;'>" +
                    "<div class='text-center' style='padding-top: 1.5px; font-size: " +
                    fontSize +
                    "px !important;" +
                    numberStyle +
                    "'>" +
                    number +
                    "</div>" +
                    "</div>";

                return;
            }

            this.labelContent =
                "<div style='border-radius: 50%; width: " +
                width +
                "; padding: " +
                padding +
                "; background: " +
                hex +
                ";'>" +
                "<div>" +
                number +
                "</div>" +
                "</div>";
        }
    }
}

angular.module("boundsService").service("MarkerService", MarkerService);
